import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import adminApiClient, {
  ApiException,
  BOAdminViewModel,
} from 'api/adminApiClient';
import { RootState } from './rootReducer';

const ALL_ADMINS_PAGE_SIZE = 100;
const ADMINS_NOT_EXISTS_ERROR_MESSAGE = 'Admins is not exist.';

interface AdminState {
  allAdmins: BOAdminViewModel[];
  isAllAdminsLoading: boolean;
}

const initialState: AdminState = {
  allAdmins: [],
  isAllAdminsLoading: false,
};

const getBOAdminsPagedList = createAsyncThunk(
  'admin/getBOAdminsPagedList',
  async ({
    pageNumber,
    pageSize,
  }: {
    pageNumber: number;
    pageSize: number;
  }) => {
    return adminApiClient.getBOAdminsPagedList(pageSize, pageNumber);
  },
);

const grantAdminRole = createAsyncThunk(
  'admin/grantAdminRole',
  async (userId: string) => {
    return adminApiClient.grantAdminRole(userId);
  },
);

const getMembersWithoutAdminRoleShort = createAsyncThunk(
  'admin/getMembersWithoutAdminRoleShort',
  async () => {
    return adminApiClient.getMembersWithoutAdminRoleShort();
  },
);

const isEmptyAdminsListError = (error: unknown) =>
  !!error &&
  typeof error === 'object' &&
  ApiException.isApiException(error) &&
  error.status === 400 &&
  (error.response.includes(ADMINS_NOT_EXISTS_ERROR_MESSAGE) ||
    error.message.includes(ADMINS_NOT_EXISTS_ERROR_MESSAGE));

const mergeAdminsById = (
  ...adminLists: BOAdminViewModel[][]
): BOAdminViewModel[] => {
  const adminsById = new Map<string, BOAdminViewModel>();

  adminLists.flat().forEach((admin) => {
    if (!admin.adminId) return;

    adminsById.set(admin.adminId, {
      ...adminsById.get(admin.adminId),
      ...admin,
    });
  });

  return Array.from(adminsById.values());
};

const getActiveAdmins = async (): Promise<BOAdminViewModel[]> => {
  try {
    return await adminApiClient.getActiveBOAdmins();
  } catch (error: unknown) {
    if (isEmptyAdminsListError(error)) {
      return [];
    }

    throw error;
  }
};

const getAllPagedAdmins = async (): Promise<BOAdminViewModel[]> => {
  try {
    const firstPage = await adminApiClient.getBOAdminsPagedList(
      ALL_ADMINS_PAGE_SIZE,
      0,
    );
    const admins = [...(firstPage.details || [])];
    const totalCount = firstPage.totalCount || 0;
    const pageCount = Math.ceil(totalCount / ALL_ADMINS_PAGE_SIZE);

    for (let pageNumber = 1; pageNumber < pageCount; pageNumber += 1) {
      const page = await adminApiClient.getBOAdminsPagedList(
        ALL_ADMINS_PAGE_SIZE,
        pageNumber,
      );
      admins.push(...(page.details || []));
    }

    return admins;
  } catch (error: unknown) {
    if (isEmptyAdminsListError(error)) {
      return [];
    }

    throw error;
  }
};

const getAllAdmins = createAsyncThunk(
  'admin/getAllAdmins',
  async () => {
    const [activeAdmins, pagedAdmins] = await Promise.all([
      getActiveAdmins(),
      getAllPagedAdmins(),
    ]);

    return mergeAdminsById(activeAdmins, pagedAdmins);
  },
  {
    condition: (_, { getState }) => {
      const { admin } = getState() as RootState;
      return !admin.allAdmins.length && !admin.isAllAdminsLoading;
    },
  },
);

const adminSlice = createSlice({
  name: 'admin',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(getAllAdmins.pending, (state) => {
      state.isAllAdminsLoading = true;
    });
    builder.addCase(getAllAdmins.fulfilled, (state, action) => {
      state.allAdmins = action.payload;
      state.isAllAdminsLoading = false;
    });
    builder.addCase(getAllAdmins.rejected, (state) => {
      state.isAllAdminsLoading = false;
    });
  },
});

const { reducer } = adminSlice;

export {
  getBOAdminsPagedList,
  grantAdminRole,
  getMembersWithoutAdminRoleShort,
  getAllAdmins,
};
export default reducer;
